import { useEffect } from 'react';
import { readSavedThemePreference, useTheme } from '@/contexts/ThemeContext';

/**
 * Force the landing page into the light theme, then restore the user's saved
 * theme when they navigate away.
 *
 * Why this exists: ThemeContext's init skips localStorage on the landing
 * path, so a hard-load of `/` already starts in `'light'`. But the provider
 * doesn't remount on SPA navigation, so:
 *   - Going INTO landing from the app: in-memory theme is still whatever the
 *     user picked. We force it to `'light'` on mount.
 *   - Going OUT of landing back to the app: in-memory theme is `'light'` and
 *     would stay that way until reload. We restore from localStorage on
 *     unmount so the app re-renders in the user's saved theme.
 *
 * Persistence is path-guarded inside ThemeContext, so calling `setTheme('light')`
 * while on `/` does NOT overwrite the saved preference. The unmount restore
 * happens after the route has already changed away from `/`, so the resulting
 * `setTheme(saved)` does write to localStorage — idempotent (writes the same
 * value back) and safe.
 */
export function useLandingTheme() {
  const { setTheme } = useTheme();

  useEffect(() => {
    setTheme('light');
    return () => {
      // Restore the saved PREFERENCE, not the resolved theme: writing a
      // concrete id back would pin a 'system' user to whatever the OS
      // happened to be (the persistence effect re-saves what we set here).
      setTheme(readSavedThemePreference());
    };
  }, [setTheme]);

  return { theme: 'light' as const };
}
